import type { PostSummary, PublicFeaturedTag, PublicPostDetail } from '@/types/api'
import type { CategoryTileModel, GalleryImage, PostCardModel, PostTagFacets } from '@/types/ui'
import { mediaUrl } from './client'
import {
  formatUpdateMonth,
  parseUpdatePeriod,
  searchPathForAuthor,
  searchPathForYear,
  tagPath,
} from './taxonomy'

/**
 * 契约形状 → 视图模型。
 *
 * 把「有没有封面」的判断收敛到这一个函数里，卡片组件只需要面对
 * `coverUrl?: string`：无封面、封面已删除或字节已清理时 `cover_public_key`
 * 整个缺省，渲染设计稿里的占位图。
 */
export function toPostCard(item: PostSummary): PostCardModel {
  const primaryAuthor = item.authors[0]
  const updated = parseUpdatePeriod(item.update_period)
  return {
    slug: item.slug,
    title: item.title,
    summary: item.summary,
    coverUrl: item.cover_public_key ? mediaUrl(item.cover_public_key) : undefined,
    coverSensitive: Boolean(item.cover_sensitive),
    authorName: primaryAuthor?.display_name,
    authorPublicId: primaryAuthor?.public_id,
    viewCount: item.view_count,
    downloadCount: item.download_count,
    firstPublishedAt: item.first_published_at,
    // 作者手填的「更新时间」，未标注时为 undefined —— 卡片退回显示发布时间。
    updatedLabel: updated ? formatUpdateMonth(updated) : undefined,
  }
}

/**
 * 展示位 → 首页磁贴。
 *
 * `tag_public_id` 而不是 `public_id`：展示位那一行自己的 id 不在公开响应里，
 * 这个字段指向的是它展示的**标签**，也正是落地页 `/t/{public_id}` 的路径参数。
 */
export function toCategoryTile(item: PublicFeaturedTag): CategoryTileModel {
  return {
    key: item.tag_public_id,
    label: item.name,
    to: tagPath({ public_id: item.tag_public_id }),
    coverUrl: item.cover_public_key ? mediaUrl(item.cover_public_key) : undefined,
    coverSensitive: Boolean(item.cover_sensitive),
  }
}

/**
 * 详情 → 需要遮罩的图片地址集合。
 *
 * 正文里的敏感标记只能从图集拿：`rendered_html` 是发布时封存的缓存，里面没有
 * 任何标记（后端把它写进去就意味着改一次标记要重渲染全部已发布修订），而图集
 * 按定义含有正文里的每一张图。所以这里把「敏感的那些」换算成 `<img src>` 里
 * 会出现的那个字符串，交给 `RenderedHtmlContent` 逐张比对。
 */
export function toSensitiveImageUrls(detail: PublicPostDetail): ReadonlySet<string> {
  const urls = new Set<string>()
  for (const image of detail.gallery) {
    if (image.sensitive) urls.add(mediaUrl(image.public_key))
  }
  return urls
}

/**
 * 详情 → 图集。
 *
 * `detail.gallery` 是派生的（封面在前，其后是正文图片按文档顺序，整体按
 * 资产去重），这里只做「能力段 → 完整 URL」的转换，不再重新排序或去重——
 * 那些规则已经在后端做过一遍，重做一遍只会在两边定义分叉时悄悄不一致。
 */
export function toGalleryImages(detail: PublicPostDetail): GalleryImage[] {
  return detail.gallery.map((image) => ({
    url: mediaUrl(image.public_key),
    alt: image.alt,
    sensitive: Boolean(image.sensitive),
  }))
}

/** 社团按 `sort_order` 排序后拼成一行文字，详情页头部用。 */
export function formatAuthorLine(detail: PublicPostDetail): string {
  return [...detail.authors]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((author) => author.display_name)
    .join(' / ')
}

/**
 * 详情 → 五栏标签。
 *
 * 三条规则值得记住：
 *
 * 1. **栏目来自后端 `tag.category`**，不再按标签名猜。后端新建的标签会自动落进
 *    它自己的那一栏，前端不需要跟着改名单。
 * 2. **更新时间来自 `detail.update_period`（`YYYY-MM`），不是标签。**
 *    展示到月，链接指向整年（`?update_year=`）。
 * 3. **社团来自 `authors` 关系**，按 `sort_order` 排序后逐个可点。
 *
 * 标签落到 `/t/{public_id}`（`tagPath`）而不是 `/search?tag_id=`：标签大全里的
 * 标签与详情页右栏的标签指向的应当是**同一个筛选结果页**，两处走不同的落地页
 * 只会让「按下载量重排」这类操作在一处有、另一处没有。作者与更新时间仍然落到
 * 搜索页，它们本来就不是标签，也没有各自的落地页。
 */
export function toTagFacets(detail: PublicPostDetail): PostTagFacets {
  const facets: PostTagFacets = { type: [], author: [], game: [], other: [], updated: [] }

  for (const tag of detail.tags) {
    facets[tag.category].push({
      key: tag.public_id,
      label: tag.name,
      to: tagPath(tag),
    })
  }

  const updated = parseUpdatePeriod(detail.update_period)
  if (updated) {
    facets.updated.push({
      key: `update-period:${detail.update_period}`,
      label: formatUpdateMonth(updated),
      to: searchPathForYear(updated.year),
    })
  }

  facets.author = [...detail.authors]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((author) => ({
      key: author.public_id,
      label: author.display_name,
      to: searchPathForAuthor(author.display_name),
    }))

  return facets
}
